import {
  createContact,
  createProfileContact,
  deleteContact,
  deleteProfileContact,
  editContact,
  editProfileContact,
  getContacts,
  getProfileContacts,
} from '../../../api'
import { EMPTY_LIST } from '../../../constants/common'
import {
  COMPANY_CONTACT_TYPE,
  PROFILE_CONTACT_TYPE,
} from '../../../constants/contact'
import { isApiBadRequestStatus } from '../../../utils/api'

import {
  EXTERNAL_CONTACT_CONTACT_TYPE_CONFIG,
  PROFILE_CONTACT_TYPE_CONFIG,
  PROFILE_CONTACTS_WIDGET_ADD_TYPES,
  PROFILE_CONTACTS_WIDGET_LIST_API_PARAMS,
  PROFILE_CONTACTS_WIDGET_MAX,
  PROVIDER_CONTACT_TYPE_CONFIG,
} from './ProfileContactsWidget.constants'
import {
  createNoValueContact,
  getCompanyTypeConfig,
  getContactValue,
  getProfileTypeConfig,
  getProviderTypeConfig,
  sortByOrder,
} from './ProfileContactsWidget.utils'

function getProfileContactsCrud({ profileId }) {
  return {
    create: ({ typeConfig, value }) =>
      createProfileContact({
        data: {
          contact_type: typeConfig.type,
          value: getContactValue({ typeConfig, value }),
        },
        id: profileId,
      }),
    edit: ({ id, typeConfig, value }) =>
      editProfileContact({
        contactId: id,
        data: { value: getContactValue({ typeConfig, value }) },
      }).then(({ data }) => data.value),
    remove: ({ id }) => deleteProfileContact({ contactId: id }),
  }
}

function getLoadedProfileContacts({ results, typeConfigMap }) {
  return (results.objects ?? EMPTY_LIST)
    .filter(contact => contact.id != null)
    .map(({ contact_type, id, permissions: contactPermissions, value }) => ({
      canDelete: contactPermissions?.can_delete,
      canEdit: contactPermissions?.can_edit,
      id,
      typeConfig: getProfileTypeConfig(contact_type, typeConfigMap),
      value,
    }))
}

export function getCompanyContactsApi({ entityId, model }) {
  return {
    create: ({ typeConfig, value }) =>
      createContact({
        data: {
          contact_type: typeConfig.type,
          fk: entityId,
          model,
          values: getContactValue({ typeConfig, value }),
        },
      }),
    edit: ({ id, typeConfig, value }) =>
      editContact({
        data: { values: getContactValue({ typeConfig, value }) },
        id,
        model,
      }).then(({ data }) => data.values),
    load: () =>
      getContacts({
        entityId,
        model,
        params: PROFILE_CONTACTS_WIDGET_LIST_API_PARAMS,
      }).then(({ data }) => ({
        addTypes: [
          getCompanyTypeConfig(COMPANY_CONTACT_TYPE.EMAIL),
          getCompanyTypeConfig(COMPANY_CONTACT_TYPE.PHONE),
          getCompanyTypeConfig(COMPANY_CONTACT_TYPE.EMERGENCY_PHONE),
          getCompanyTypeConfig(COMPANY_CONTACT_TYPE.WEBSITE),
        ],
        canCreate: true,
        contacts: data.results.objects.map(({ contact_type, id, values }) => ({
          canDelete: true,
          canEdit: true,
          id,
          typeConfig: getCompanyTypeConfig(contact_type),
          value: values,
        })),
        limit: PROFILE_CONTACTS_WIDGET_MAX,
      })),
    remove: ({ id }) => deleteContact({ id, model }),
  }
}

// Only the types of `typeConfigMap` are shown; the auth email/phone stubs
// (id: null) become editable placeholder rows when the map lists them.
export function getProfileContactsApi({
  profileId,
  typeConfigMap = PROFILE_CONTACT_TYPE_CONFIG,
}) {
  return {
    ...getProfileContactsCrud({ profileId }),
    load: () =>
      getProfileContacts({
        id: profileId,
        params: PROFILE_CONTACTS_WIDGET_LIST_API_PARAMS,
      }).then(({ data }) => {
        const { permissions, results } = data

        const objects = (results.objects ?? EMPTY_LIST).filter(
          contact => typeConfigMap[contact.contact_type]
        )

        const contacts = getLoadedProfileContacts({
          results: { objects },
          typeConfigMap,
        })

        const authStubContacts = objects
          .filter(contact => contact.id == null)
          .map(contact =>
            createNoValueContact({
              canEdit: contact.permissions?.can_edit,
              typeConfig: getProfileTypeConfig(
                contact.contact_type,
                typeConfigMap
              ),
            })
          )

        return {
          addTypes: sortByOrder({
            getConfig: config => config,
            items: PROFILE_CONTACTS_WIDGET_ADD_TYPES.map(type =>
              getProfileTypeConfig(type, typeConfigMap)
            ),
          }),
          canCreate: permissions?.can_create,
          contacts: sortByOrder({
            getConfig: contact => contact.typeConfig,
            items: [...contacts, ...authStubContacts],
          }),
          limit: PROFILE_CONTACTS_WIDGET_MAX,
        }
      }),
  }
}

export function getExternalContactContactsApi({ profileId }) {
  return getProfileContactsApi({
    profileId,
    typeConfigMap: EXTERNAL_CONTACT_CONTACT_TYPE_CONFIG,
  })
}

export function getProviderContactsApi({ profileId }) {
  const crud = getProfileContactsCrud({ profileId })

  return {
    ...crud,
    // A hidden empty-value row of the same type may already exist (the legacy
    // provider form saves blanks as ''), and the backend allows one contact
    // per type — update that row instead of failing the create.
    create: params =>
      crud.create(params).catch(error => {
        if (!isApiBadRequestStatus(error?.response?.status)) {
          throw error
        }

        return getProfileContacts({
          id: profileId,
          params: PROFILE_CONTACTS_WIDGET_LIST_API_PARAMS,
        }).then(({ data }) => {
          const existingContact = (data.results.objects ?? EMPTY_LIST).find(
            contact =>
              contact.id != null &&
              contact.contact_type === params.typeConfig.type &&
              !contact.value
          )

          if (!existingContact) {
            throw error
          }

          return crud.edit({ ...params, id: existingContact.id })
        })
      }),
    load: () =>
      getProfileContacts({
        id: profileId,
        params: PROFILE_CONTACTS_WIDGET_LIST_API_PARAMS,
      }).then(({ data }) => {
        const { permissions, results } = data

        // The contractor list mixes in auth-type stubs (id: null), flat header
        // fields (address, working hours) and legacy empty-value rows — only
        // filled contacts of the provider widget types are rows here.
        const contacts = getLoadedProfileContacts({
          results: {
            objects: (results.objects ?? EMPTY_LIST).filter(
              contact =>
                PROVIDER_CONTACT_TYPE_CONFIG[contact.contact_type] &&
                contact.value
            ),
          },
          typeConfigMap: PROVIDER_CONTACT_TYPE_CONFIG,
        })

        const mainEmailConfig = getProviderTypeConfig(
          PROFILE_CONTACT_TYPE.EMAIL
        )

        const hasMainEmail = contacts.some(
          contact => contact.typeConfig.type === mainEmailConfig.type
        )

        return {
          addTypes: sortByOrder({
            getConfig: config => config,
            items: [
              mainEmailConfig,
              getProviderTypeConfig(PROFILE_CONTACT_TYPE.PHONE),
              getProviderTypeConfig(PROFILE_CONTACT_TYPE.WEBSITE),
            ],
          }),
          canCreate: permissions?.can_create,
          contacts: sortByOrder({
            getConfig: contact => contact.typeConfig,
            items: hasMainEmail
              ? contacts
              : [
                  ...contacts,
                  createNoValueContact({ typeConfig: mainEmailConfig }),
                ],
          }),
          // Every provider type is unique, so the dropdown itself caps the
          // contact count.
          limit: null,
        }
      }),
  }
}
